import { useTranslation } from "react-i18next";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Check, Trophy } from "lucide-react";
import { cn } from "@/lib/utils";

interface ProgressIndicatorProps {
  completed: number;
  total: number;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function ProgressIndicator({
  completed,
  total,
  showLabel = true,
  size = 'md',
  className,
}: ProgressIndicatorProps) {
  const { t } = useTranslation();
  const percentage = total > 0 ? (completed / total) * 100 : 0;
  const isComplete = completed === total && total > 0;

  return (
    <div className={cn("space-y-1", className)}>
      {showLabel && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            {t('latexCenter.progress.completed', '{{completed}}/{{total}} completed', { completed, total })}
          </span>
          {isComplete && (
            <Badge variant="default" className="bg-green-600 hover:bg-green-700">
              <Check className="h-3 w-3 mr-1" />
              {t('latexCenter.progress.complete', 'Complete')}
            </Badge>
          )}
        </div>
      )}
      <Progress 
        value={percentage} 
        className={cn(
          size === 'sm' && "h-1",
          size === 'md' && "h-2",
          size === 'lg' && "h-3",
          isComplete && "[&>div]:bg-green-600"
        )}
      />
    </div>
  );
}

interface ModuleProgressBadgeProps {
  moduleName: string;
  completed: number;
  total: number;
}

export function ModuleProgressBadge({ moduleName, completed, total }: ModuleProgressBadgeProps) {
  const { t } = useTranslation();
  const isComplete = completed === total && total > 0;
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

  if (isComplete) {
    return (
      <Badge className="bg-green-600 hover:bg-green-700">
        <Trophy className="h-3 w-3 mr-1" />
        {t('latexCenter.progress.moduleComplete', 'Module Complete')}
      </Badge>
    );
  }

  if (completed > 0) {
    return (
      <Badge variant="secondary">
        {percentage}%
      </Badge>
    );
  }

  return null;
}
